import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { categories, type DocsArticle, type DocsSection } from '@/routes/docs/_data'

interface SearchResult {
  article: DocsArticle
  section?: DocsSection
  matchType: 'title' | 'description' | 'section'
}

export function DocsSearch() {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const allArticles = useMemo(
    () => categories.flatMap((c) => c.articles),
    []
  )

  const results = useMemo(() => {
    if (query.length < 2) return []
    const q = query.toLowerCase()
    const matches: SearchResult[] = []

    for (const article of allArticles) {
      if (article.title.toLowerCase().includes(q)) {
        matches.push({ article, matchType: 'title' })
        continue
      }
      if (article.description.toLowerCase().includes(q)) {
        matches.push({ article, matchType: 'description' })
        continue
      }
      for (const section of article.sections) {
        if (section.heading.toLowerCase().includes(q)) {
          matches.push({ article, section, matchType: 'section' })
          break
        }
      }
    }

    return matches.slice(0, 8)
  }, [query, allArticles])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (result: SearchResult) => {
    const hash = result.section ? `#${result.section.id}` : ''
    navigate(`/docs/${result.article.slug}${hash}`)
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/40" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search docs..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          className="w-full bg-muted/30 border border-border/60 rounded-lg pl-9 pr-9 py-2 text-[14px] placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-ring/20 focus:border-border transition-colors"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setOpen(false) }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted transition-colors"
          >
            <X className="h-3 w-3 text-muted-foreground/50" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          {results.map((result, i) => (
            <button
              key={`${result.article.slug}-${result.section?.id ?? 'root'}`}
              onClick={() => handleSelect(result)}
              className={cn(
                'w-full text-left px-3 py-2 text-[13px] hover:bg-accent/60 transition-colors flex flex-col gap-0.5',
                i > 0 && 'border-t border-border/30'
              )}
            >
              <span className="font-medium text-foreground truncate">{result.article.title}</span>
              {result.section && (
                <span className="text-[11px] text-muted-foreground/60 truncate">
                  {result.section.heading}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {open && query.length >= 2 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 p-3">
          <p className="text-[12px] text-muted-foreground/50 text-center">No results found</p>
        </div>
      )}
    </div>
  )
}
